import axios from 'axios';

import { SeverityLevel } from '@microsoft/applicationinsights-web';

import { getHeader, setHeader } from '../../api/commonHTTP';
import { appInsights } from '../../ApplicationInsightsService';
import { msalConfig, msalInstance } from '../../masl/authConfig';
import { beginLogout } from '../../masl/authState';
import { trimEntries } from '../../utils/objectUtils';
import * as actions from '../api';
import { setErrorMessage, setSuccessMessage } from '../formsState';
import { clearLocalStorage, getAccessToken, saveAccessToken, setIdToken } from '../userService';

const api =
    ({ dispatch }) =>
    (next) =>
    async (action) => {
        if (
            action.type !== actions.apiUpload.type &&
            action.type !== actions.apiDownload.type &&
            action.type !== actions.apiCallBegan.type &&
            action.type !== actions.apiCallDirectusBegan.type &&
            action.type !== actions.apiSeqDelete.type &&
            action.type !== actions.apiSeqAdd.type &&
            action.type !== actions.apiVersion.type
        )
            return next(action);

        const {
            url,
            method,
            data,
            filename,
            onStart,
            onSuccess,
            onError,
            onBusinessLogicError,
            onValidationError,
            successMessage,
            origin,
            onStartData
        } = action.payload;

        const startData = onStartData || data || '';
        if (onStart) dispatch({ type: onStart, payload: startData });

        next(action);

        if (action.type === actions.apiCallBegan.type || action.type === actions.apiVersion.type) {
            try {
                const headers =
                    action.type === actions.apiCallBegan.type
                        ? getHeader()
                        : trimEntries(['Authorization'], getHeader());
                const response = await axios.request({
                    url,
                    headers,
                    method,
                    data
                });
                // General success dispatch
                dispatch(actions.apiCallSuccess(response.data));

                if (successMessage) dispatch(setSuccessMessage(successMessage));

                // Specific success dispatch if present
                if (onSuccess) dispatch({ type: onSuccess, payload: response.data });
            } catch (error) {
                // General error action
                dispatch(actions.apiCallFailed(error.message));
                // validation errors
                if (error?.response?.data?.validationErrors) {
                    if (onValidationError) {
                        // Send validation errors back for FE to display on inputs -
                        dispatch({
                            type: onValidationError,
                            error: error?.response.data.validationErrors
                        });
                    } else {
                        // Tell formsState slice that error has occurred.
                        dispatch(
                            setErrorMessage(
                                `${Object.keys(
                                    error?.response?.data?.validationErrors
                                ).toString()} ${Object.values(
                                    error?.response?.data?.validationErrors
                                ).toString()}`
                            )
                        );
                    }
                } else if (error?.response?.data?.businessLogicError && onBusinessLogicError) {
                    // Send businessLogic errors back for FE to display on component screen -
                    dispatch({
                        type: onBusinessLogicError,
                        error: error?.response.data
                    });
                } else if (error?.response?.data) {
                    const errorMessage = getErrorMessage(
                        error?.response,
                        error.message
                    );
                    dispatch(setErrorMessage(errorMessage));
                    if (onError) dispatch({ type: onError, error: error?.response?.data });
                }
                // Specific error - dispatch if present
                else if (onError) dispatch({ type: onError, payload: error.message });
                else dispatch(setErrorMessage(error.message));
            }
        } else if (action.type === actions.apiUpload.type) {
            try {
                const response = await axios.request({
                    url,
                    headers: { ...getHeader(), 'Content-Type': 'multipart/form-data' },
                    method,
                    data
                });
                // General success dispatch
                dispatch(actions.apiCallSuccess(response.data));

                if (successMessage) dispatch(setSuccessMessage(successMessage));

                // Specific success dispatch if present
                if (onSuccess) dispatch({ type: onSuccess, payload: response.data });
            } catch (error) {
                // General error action
                dispatch(actions.apiCallFailed(error.message));
                // validation errors
                if (error?.response?.data?.validationErrors) {
                    if (onValidationError) {
                        // Send validation errors back for FE to display on inputs -
                        dispatch({
                            type: onValidationError,
                            error: error?.response.data.validationErrors
                        });
                    } else {
                        // Tell formsState slice that error has occurred.
                        dispatch(
                            setErrorMessage(
                                `${Object.keys(
                                    error?.response?.data?.validationErrors
                                ).toString()} ${Object.values(
                                    error?.response?.data?.validationErrors
                                ).toString()}`
                            )
                        );
                    }
                } else if (error?.response?.data) {
                    if (onError) dispatch({ type: onError, error: error?.response?.data });
                    else setErrorMessage(error?.response?.data);
                }
                // Specific error - dispatch if present
                else if (onError) dispatch({ type: onError, payload: error.message });
                else dispatch(setErrorMessage(error.message));
            }
        } else if (action.type === actions.apiDownload.type) {
            try {
                const response = await axios.request({
                    url,
                    headers: getHeader(),
                    method,
                    responseType: 'blob'
                });
                const href = window.URL.createObjectURL(response.data);
                const anchorElement = document.createElement('a');
                anchorElement.href = href;
                anchorElement.download = filename;
                document.body.appendChild(anchorElement);
                anchorElement.click();
                document.body.removeChild(anchorElement);
                window.URL.revokeObjectURL(href);
            } catch (error) {
                let message;
                // General error action
                if (error?.response?.data?.errors[0]?.message)
                    message = error.response.data.errors[0].message;
                else message = 'An error occurred during download';

                dispatch(actions.apiCallFailed(message));
            }
        } else if (action.type === actions.apiSeqDelete.type) {
            try {
                const BATCH_SIZE = 500;
                if (data.keys.length <= BATCH_SIZE) {
                    await axios.request({
                        url,
                        headers: getHeader(),
                        method,
                        data
                    });
                } else {
                    const entries = data.keys;
                    let count = 0;
                    const numBatches = Math.ceil(entries.length / BATCH_SIZE);
                    while (count < numBatches) {
                        const keys = entries.splice(0, BATCH_SIZE);
                        if (keys.length) {
                            const data = { keys };
                            await axios.request({
                                url,
                                headers: getHeader(),
                                method,
                                data
                            });
                        }
                        count++;
                    }
                }
                if (successMessage) dispatch(setSuccessMessage(successMessage));
                if (onSuccess) dispatch({ type: onSuccess });
            } catch (error) {
                let message;
                if (error?.response?.data?.errors && error?.response?.data?.errors[0]?.message)
                    message = error.response.data.errors[0].message;
                else message = 'An error has occurred';
                dispatch(actions.apiCallFailed(message));
                dispatch(setErrorMessage(message));
                if (onError) dispatch({ type: onError, payload: { origin, message } });
            }
        } else if (action.type === actions.apiSeqAdd.type) {
            try {
                const BATCH_SIZE = 500;
                let response;
                if (data.length <= BATCH_SIZE) {
                    response = await axios.request({
                        url,
                        headers: getHeader(),
                        method,
                        data
                    });
                } else {
                    let count = 0;
                    const numBatches = Math.ceil(data.length / BATCH_SIZE);
                    while (count < numBatches) {
                        const entries = data.splice(0, BATCH_SIZE);
                        if (entries.length) {
                            response = await axios.request({
                                url,
                                headers: getHeader(),
                                method,
                                data: entries
                            });
                        }
                        count++;
                    }
                }
                dispatch(actions.apiCallSuccess(response.data));
                if (successMessage) dispatch(setSuccessMessage(successMessage));
                if (onSuccess)
                    dispatch({
                        type: onSuccess,
                        payload: response.data,
                        status: response.status
                    });
            } catch (error) {
                let message;
                if (error?.response?.data?.errors && error?.response?.data?.errors[0]?.message)
                    message = error.response.data.errors[0].message;
                else message = 'An error has occurred';
                dispatch(actions.apiCallFailed(message));
                dispatch(setErrorMessage(message));
                if (onError) dispatch({ type: onError, payload: { origin, message } });
            }
        } else {
            // Must be a Directus call
            try {
                const response = await axios.request({
                    url,
                    headers: getHeader(),
                    method,
                    data
                });

                // General success dispatch
                dispatch(actions.apiCallSuccess(response.data));
                if (successMessage) dispatch(setSuccessMessage(successMessage));
                // Specific success dispatch if present
                if (onSuccess)
                    dispatch({
                        type: onSuccess,
                        payload: response.data,
                        status: response.status
                    });
            } catch (error) {
                let message;
                // General error action
                if (error?.response?.data?.errors && error?.response?.data?.errors[0]?.message)
                    message = error.response.data.errors[0].message;
                else if (error?.response?.data?.validationErrors?.title)
                    message = error?.response?.data?.validationErrors?.title;
                else message = 'An error has occurred';

                dispatch(actions.apiCallFailed(message));

                // Tell formsState slice that error has occurred.
                dispatch(setErrorMessage(message));
                // Specific error to the fn that called the req - dispatch if present
                if (onError) dispatch({ type: onError, payload: { origin, message } });
            }
        }
    };

let isRefreshing = false;
let refreshSubscribers = [];

const getTokenExpired = (token) => {
    const bufferTime = 30; // Buffer seconds before actual expiration, to make room for pending requests
    try {
        const payloadBase64 = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payloadBase64));
        const expiresOn = new Date((decodedPayload.exp - bufferTime) * 1000);
        return expiresOn < new Date();
    } catch {
        return true;
    }
};

const ensureMsalInitialized = async () => {
    if (!msalInstance || !msalInstance.initialize) {
        console.error('msalInstance is not available!');
        return false;
    }

    try {
        await msalInstance.initialize(); // Ensure it's initialized
        return true;
    } catch (error) {
        console.error('Error initializing msalInstance:', error);
        return false;
    }
};

const refreshToken = async (token) => {
    if (isRefreshing) {
        return new Promise((resolve) => {
            refreshSubscribers.push(resolve);
        });
    }

    isRefreshing = true;
    const isInitialized = await ensureMsalInitialized();
    if (!isInitialized) {
        isRefreshing = false;
        return null;
    }
    try {
        const request = {
            scopes: []
        };
        let tokenResponse;
        try {
            // Try ssoSilent first (iframe-based) so the B2C user journey runs
            // and session revocation checks are enforced
            tokenResponse = await msalInstance.ssoSilent(request);
        } catch (ssoError) {
            // If session was revoked, re-throw so the ACQUIRE_TOKEN_FAILURE handler logs the user out
            if (ssoError?.errorMessage?.includes('Error_SessionRevoked')) {
                throw ssoError;
            }
            // ssoSilent failed for other reasons (iframe/cookie issues, e.g. SSO users)
            // Fall back to acquireTokenSilent (refresh token) to keep the session alive
            tokenResponse = await msalInstance.acquireTokenSilent(request);
        }
        const newIdToken = tokenResponse.idToken;

        if (newIdToken) {
            saveAccessToken(newIdToken);
            setIdToken(newIdToken);
            setHeader({
                Authorization: 'Bearer ' + newIdToken + '',
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json'
            });
            refreshSubscribers.forEach((callback) => callback(newIdToken));
            refreshSubscribers = [];
            return newIdToken;
        } else {
            return token;
        }
    } catch (error) {
        console.error('Error refreshing token:', error);
        if (beginLogout()) {
            const logoutReason = error?.errorMessage?.includes('Error_SessionRevoked')
                ? 'session_revoked'
                : 'session_expired';
            clearLocalStorage();
            sessionStorage.setItem('logoutReason', logoutReason);
            await msalInstance.logoutRedirect({
                account: msalInstance.getActiveAccount(),
                postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
            });
        }
        return null;
    } finally {
        isRefreshing = false;
    }
};

const getErrorMessage = (response, errorMessage) => {
    if (!response?.status) return 'Network error - no response received from server.';

    if (response.status === 504) return '504 - Gateway timeout.';
    if (response.status === 503) return '503 - Service unavailable.';
    if (response.status === 502) return '502 - Bad gateway.';
    if (response.status === 501) return '501 - Not implemented.';
    if (response.status === 403) return '403 - Forbidden.';

    if (!errorMessage) return 'An error has occurred.';

    return errorMessage;
};


const logTraceToAppInsights = (error) => {
    const errorMessage = getErrorMessage(error?.response, error?.message);

    appInsights.trackTrace({
        message: 'Error trace for : ' + errorMessage,
        severityLevel: SeverityLevel.Error,
        properties: {
            errorMessage: errorMessage,
            requestMethod: error.config.method,
            requestUrl: error.config.url,
            requestData: error.config.data,
            responseData: error.response.data,
            responseStatus: error.response.status
        }
    });
};

axios.interceptors.request.use(
    async (config) => {
        if (config.headers['Authorization']) {
            let token = getAccessToken();
            if (config.headers['Authorization'].replace(/^Bearer\s+/i, '') !== token) {
                setHeader({
                    Authorization: 'Bearer ' + token + '',
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json'
                });
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            if (token && getTokenExpired(token)) {
                token = await refreshToken(token);
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => error
);

axios.interceptors.response.use(
    async (response) => response,
    async (error) => {
        if (error.response && error.response.status === 401) {
            if (beginLogout()) {
                clearLocalStorage();
                sessionStorage.setItem('logoutReason', 'account_inactive');
                await msalInstance.logoutRedirect({
                    account: msalInstance.getActiveAccount(),
                    postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
                });
            }
        } else {
            logTraceToAppInsights(error);
            throw error;
        }
    }
);

export default api;
